import { useEffect, useRef, useState } from 'react'
import {
    EntityListWithPagination,
    EntityListWithPaginationFields,
    EntityPagination,
    EntityPaginationFields,
    PaginacionRequest

    // QueryStatus
} from 'src/types'

// type FilterParam<TFilter> = TFilter extends undefined ? [] : [filter: TFilter]

// type UsePaginationProps<TEntity, TFilter> = {
//     loader: (
//         paginationParams: PaginacionRequest,
//         ...filterParams: FilterParam<TFilter>
//     ) => Promise<EntityListWithPagination<TEntity>>
// }

// const QUERY_STATUS = {
//     IDLE: 'idle',
//     LOADING: 'loading',
//     SUCCESS: 'success',
//     ERROR: 'error'
// } as const

type UsePaginationProps<TEntity, TFilter> = {
    loader: (paginationParams: PaginacionRequest, filter: TFilter) => Promise<EntityListWithPagination<TEntity>>
    initialFilter?: TFilter
}

type StateObject<TEntity> =
    | {
          status: 'idle'
      }
    | {
          status: 'loading'
      }
    | {
          status: 'success'
          entities: TEntity[]
          pagination: EntityPagination
      }
    | {
          status: 'error'
      }

/**
 * Hook para manejar entidades con paginación. Recibe la función que carga los
 * datos y le inyecta los parámetros de paginación.
 * Devuelve la lista de entidades de la página actual, la información de
 * paginación y el handler "onChange", que debería recibir la página actual.
 */
export const usePagination = <TEntity, TFilter = undefined>(props: UsePaginationProps<TEntity, TFilter>) => {
    const { loader } = props

    const [state, setState] = useState<StateObject<TEntity>>({ status: 'idle' })
    const [filter, setFilter] = useState<TFilter | undefined>(props.initialFilter && props.initialFilter)

    const [paramsPaginacion, setParamsPaginacion] = useState<PaginacionRequest>({
        [EntityPaginationFields.ActualPage]: 1,
        [EntityPaginationFields.PageSize]: 24
    })

    const fetchGeneration = useRef(0)

    const onChange = (/*event: React.ChangeEvent,*/ page: number) => {
        setParamsPaginacion(params => ({
            ...params,
            [EntityPaginationFields.ActualPage]: page
        }))
    }

    const page = paramsPaginacion[EntityPaginationFields.ActualPage]

    const fetch = () => {
        if (filter === undefined) return

        const generation = ++fetchGeneration.current
        setState({ status: 'loading' })

        loader(paramsPaginacion, filter as TFilter)
            .then(vals => {
                if (generation !== fetchGeneration.current) return
                setState({
                    status: 'success',
                    pagination: vals[EntityListWithPaginationFields.Pagination],
                    entities: vals[EntityListWithPaginationFields.List]
                })
            })
            .catch(() => {
                if (generation !== fetchGeneration.current) return
                setState({ status: 'error' })
            })
    }

    const setFilterAndSearch = (filter: TFilter) => {
        setFilter({ ...filter })
    }

    useEffect(() => {
        fetch()
    }, [page, filter])

    if (state.status === 'success') {
        return {
            entities: state.entities,
            paginacion: state.pagination,
            status: state.status,
            refetch: fetch,
            onChange,
            setFilterAndSearch
        }
    }

    return { status: state.status, refetch: fetch, onChange, setFilterAndSearch }
}
